import React from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import FormatQuoteOutlinedIcon from "@mui/icons-material/FormatQuoteOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";

import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";
import { useGetSubscriptionQuery } from "../../../app/subscriptionApiSlice";
import { formatUtcDatetime } from "../../../helpers/dateUtils";

export default function SchoolInformation() {
  const theme = useTheme();

  // Queries
  const {
    data: schoolProfile,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useGetSchoolProfileQuery();

  const {
    data: subData,
    isLoading: isSubLoading,
    refetch: refetchSub,
  } = useGetSubscriptionQuery();

  const isLoading = isProfileLoading || isSubLoading;

  const handleRefreshAll = () => {
    refetchProfile();
    refetchSub();
  };

  // Helper formatting for values
  const plan = subData?.plan || subData?.subscription || {};
  const wallets = subData?.wallets || {};
  const usage = subData?.usage || {};

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "trial":
        return "info";
      case "cancelled":
      case "warning":
        return "warning";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading school information...
        </Typography>
      </Box>
    );
  }

  if (profileError) {
    return (
      <Box sx={{ p: 3 }}>
        <StyledPaper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load school information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {profileError?.data?.message ||
              profileError?.error ||
              "An error occurred while fetching school profile details."}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRefreshAll}
            startIcon={<RefreshOutlinedIcon />}
          >
            Retry
          </Button>
        </StyledPaper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            School Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive overview of school profile
          </Typography>
        </Box>
      </Box>

      {/* Hero Banner Paper */}
      <StyledPaper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
              : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={schoolProfile?.logo}
              alt={schoolProfile?.name || "School Logo"}
              sx={{
                width: 96,
                height: 96,
                borderRadius: 3,
                boxShadow: 3,
                bgcolor: "primary.main",
                fontSize: "2rem",
                fontWeight: 700,
              }}
            >
              {!schoolProfile?.logo &&
                (schoolProfile?.name?.[0]?.toUpperCase() || (
                  <SchoolOutlinedIcon fontSize="large" />
                ))}
            </Avatar>
          </Grid>

          <Grid item xs={12} sm>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {schoolProfile?.name || "School Name Not Available"}
                </Typography>
                {schoolProfile?.alias && (
                  <Chip
                    label={schoolProfile.alias}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                  />
                )}
              </Box>

              {schoolProfile?.motto && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <FormatQuoteOutlinedIcon
                    fontSize="small"
                    sx={{ transform: "rotate(180deg)" }}
                  />
                  {schoolProfile.motto}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pt: 0.5 }}>
                {plan?.display_name || plan?.name ? (
                  <Chip
                    icon={<CardMembershipOutlinedIcon fontSize="small" />}
                    label={`Plan: ${plan.display_name || plan.name}`}
                    color={getStatusColor(plan.status || "active")}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    icon={<CardMembershipOutlinedIcon fontSize="small" />}
                    label="Plan: Standard"
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}

                {subData?.billing_mode && (
                  <Chip
                    label={`Billing: ${subData.billing_mode === "parent_pays" ? "Parent Pays" : "School Pays"}`}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Main Grid Content */}
      <Grid container spacing={3}>
        {/* Left Column: School Profile & Contact Details */}
        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SchoolOutlinedIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                School Contact & Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Stack spacing={2.5}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <EmailOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Official Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {schoolProfile?.email ? (
                      <a
                        href={`mailto:${schoolProfile.email}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        {schoolProfile.email}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <PhoneOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {schoolProfile?.phone ? (
                      <a
                        href={`tel:${schoolProfile.phone}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        {schoolProfile.phone}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <LanguageOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Website URL
                  </Typography>
                  {schoolProfile?.website ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      <a
                        href={
                          schoolProfile.website.startsWith("http")
                            ? schoolProfile.website
                            : `https://${schoolProfile.website}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: "none",
                        }}
                      >
                        {schoolProfile.website}
                      </a>
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Not provided
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <LocationOnOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Address
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {schoolProfile?.address || "Not provided"}
                  </Typography>
                </Box>
              </Box>

              {schoolProfile?.recording_deadline && (
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <EventBusyOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Score Recording Deadline
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "warning.main" }}
                    >
                      {formatUtcDatetime(schoolProfile.recording_deadline)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {schoolProfile?.created_at && (
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <CalendarTodayOutlinedIcon color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Onboarded / Registered
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatUtcDatetime(schoolProfile.created_at)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </StyledPaper>
        </Grid>

        {/* Right Column: Subscription & Wallets */}
        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ p: 3, height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CardMembershipOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Subscription & Billing
                </Typography>
              </Box>
              {plan?.status && (
                <Chip
                  label={plan.status.toUpperCase()}
                  color={getStatusColor(plan.status)}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Current Package
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: "primary.main" }}
                    >
                      {plan?.display_name || plan?.name || "Free Tier"}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Plan Expiration
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {plan?.ends_at
                        ? formatUtcDatetime(plan.ends_at)
                        : "No Expiry / Lifetime"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Wallets & Credits Summary */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AccountBalanceWalletOutlinedIcon
                    fontSize="small"
                    color="primary"
                  />
                  PAYG Balances & Wallets
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        AI Credits
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {wallets?.ai_credits ?? usage?.ai_credits_balance ?? 0}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Report Credits
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {wallets?.report_credits ??
                          usage?.report_credits_balance ??
                          0}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        SMS Credits
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {wallets?.sms_credits ??
                          usage?.sms_credits_balance ??
                          0}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
}
